import {
    IonButton,
    IonImg,
    IonItem,
    IonLabel,
    IonRow,
    IonSpinner,
} from '@ionic/react';
import { Trans } from '@lingui/macro';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

type SignatureProps = {
    onChange: Function;
    label: string;
    signatureImageUrl?: string;
    className?: string;
};

const Signature: React.FunctionComponent<SignatureProps> = ({
    onChange,
    label,
    signatureImageUrl,
    className,
}): JSX.Element => {
    const signatureEl = useRef<SignatureCanvas>(null);
    const canvasWidth = document.documentElement.clientWidth - 32 - 4;
    const [loading, setLoading] = useState(false);
    const [image, setImage] = useState<string | undefined>(undefined);
    const [showSignaturePad, setShowSignaturePad] = useState(
        !signatureImageUrl
    );

    const onClear = useCallback(() => {
        if (signatureEl.current) {
            signatureEl.current.clear();
        }
        onChange();
    }, [onChange]);

    const onEnd = useCallback(() => {
        if (signatureEl.current) {
            onChange(signatureEl.current.toDataURL('image/jpeg'));
        }
    }, [onChange]);

    const onUpdateSignature = useCallback(() => {
        setShowSignaturePad((showSignaturePad) => !showSignaturePad);
    }, []);

    useEffect(() => {
        const fetchSignatureImage = async () => {
            if (signatureImageUrl) {
                setLoading(true);
                const response = await fetch(signatureImageUrl, {
                    credentials: 'include',
                });
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                setImage(objectUrl);
            }
        };

        fetchSignatureImage();
    }, [signatureImageUrl]);

    return (
        <div className={className}>
            <IonRow>
                <IonLabel position="stacked">{label}</IonLabel>
            </IonRow>
            {showSignaturePad && (
                <>
                    <IonRow style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <SignatureCanvas
                            ref={signatureEl}
                            penColor="black"
                            onEnd={onEnd}
                            backgroundColor="rgb(255, 255, 255)"
                            canvasProps={{
                                width: canvasWidth,
                                height: 200,
                                className: 'signature-canvas',
                                style: {
                                    border: '2px solid rgb(118, 118, 118)',
                                    borderRadius: '6px',
                                },
                            }}
                        />
                    </IonRow>
                    <IonRow>
                        <IonButton
                            size="small"
                            onClick={onClear}
                            expand="block"
                        >
                            <Trans>Clear</Trans>
                        </IonButton>
                    </IonRow>
                </>
            )}
            {!showSignaturePad && (
                <>
                    <IonImg
                        src={image}
                        onIonImgWillLoad={() => setLoading(true)}
                        onIonImgDidLoad={() => {
                            image && URL.revokeObjectURL(image);
                            setLoading(false);
                        }}
                    ></IonImg>
                    {loading && (
                        <IonRow className="ion-justify-content-center ion-align-items-center ion-padding">
                            <IonSpinner
                                name="crescent"
                                className="ion-margin-end"
                            />
                            <IonLabel>Loading Signature</IonLabel>
                        </IonRow>
                    )}
                    <IonItem lines="full" className="full">
                        <IonButton size="small" onClick={onUpdateSignature}>
                            <Trans>Sign again</Trans>
                        </IonButton>
                    </IonItem>
                </>
            )}
        </div>
    );
};

export default Signature;
